import type { FormMiddleStopPaint } from '@/components/shipment/form/domain/map-paint-types';

export function middleStopPaintFingerprint(
  m: FormMiddleStopPaint | null | undefined,
): string {
  if (!m) return '';
  const gid = m.geofence?.id ?? '';
  const { lat, lng } = m.coordinates;
  return `${gid}|${lat}|${lng}|${m.radiusM}`;
}

export function isRenderableMiddleStopPaint(
  m: FormMiddleStopPaint | undefined,
): boolean {
  if (!m) return false;
  if (m.geofence) return true;
  return Number.isFinite(m.coordinates.lat) && Number.isFinite(m.coordinates.lng);
}

function middleStopOnlyRadiusChangedWhileGeofence(
  n: FormMiddleStopPaint | undefined,
  o: FormMiddleStopPaint | undefined,
): boolean {
  if (!n || !o) return false;
  if (!n.geofence || !o.geofence || n.geofence.id !== o.geofence.id) return false;
  const core = (m: FormMiddleStopPaint) =>
    `${m.geofence!.id}|${m.coordinates.lat}|${m.coordinates.lng}`;
  return core(n) === core(o) && n.radiusM !== o.radiusM;
}

export function formMiddleSliceOnlyRadiusChangeWithGeofence(
  newArr: (FormMiddleStopPaint | null)[],
  oldArr: (FormMiddleStopPaint | null)[],
): boolean {
  if (newArr.length <= 2 || newArr.length !== oldArr.length) return false;
  let sawChange = false;
  for (let i = 1; i < newArr.length - 1; i++) {
    const n = newArr[i];
    const o = oldArr[i];
    if (middleStopPaintFingerprint(n) === middleStopPaintFingerprint(o)) continue;
    sawChange = true;
    if (!middleStopOnlyRadiusChangedWhileGeofence(n ?? undefined, o ?? undefined)) {
      return false;
    }
  }
  return sawChange;
}

export function findFocusStopIndexFromPaintChange(
  newArr: (FormMiddleStopPaint | null)[],
  oldArr: (FormMiddleStopPaint | null)[],
): number | undefined {
  if (formMiddleSliceOnlyRadiusChangeWithGeofence(newArr, oldArr)) {
    return undefined;
  }

  for (let i = 1; i < newArr.length - 1; i++) {
    const n = newArr[i];
    const o = oldArr[i];
    if (middleStopPaintFingerprint(n) === middleStopPaintFingerprint(o)) continue;
    if (middleStopOnlyRadiusChangedWhileGeofence(n ?? undefined, o ?? undefined)) {
      continue;
    }
    if (isRenderableMiddleStopPaint(n ?? undefined)) return i;
  }

  if (newArr.length === oldArr.length) {
    let onlyEndpointsChanged = true;
    for (let i = 1; i < newArr.length - 1; i++) {
      if (middleStopPaintFingerprint(newArr[i]) !== middleStopPaintFingerprint(oldArr[i])) {
        onlyEndpointsChanged = false;
        break;
      }
    }
    if (onlyEndpointsChanged) {
      if (middleStopPaintFingerprint(newArr[0]) !== middleStopPaintFingerprint(oldArr[0])) {
        return 0;
      }
      if (
        middleStopPaintFingerprint(newArr[newArr.length - 1]) !==
        middleStopPaintFingerprint(oldArr[newArr.length - 1])
      ) {
        return newArr.length - 1;
      }
    }
  }

  return undefined;
}

export function stopsPaintChanged(
  newArr: (FormMiddleStopPaint | null)[],
  oldArr: (FormMiddleStopPaint | null)[],
): boolean {
  if (newArr.length !== oldArr.length) return true;
  for (let i = 0; i < newArr.length; i++) {
    if (middleStopPaintFingerprint(newArr[i]) !== middleStopPaintFingerprint(oldArr[i])) {
      return true;
    }
  }
  return false;
}

export function middleStopsDenseFromForm(
  arr: (FormMiddleStopPaint | null)[],
): FormMiddleStopPaint[] {
  if (arr.length <= 2) return [];
  return arr.slice(1, -1).filter((x): x is FormMiddleStopPaint => x != null);
}
